import React, { useState, useEffect, useMemo, CSSProperties } from 'react';
import { useTranslation } from 'react-i18next';
import './MyLeaves.scss';
import { Table } from 'antd';
import { Tooltip, Tag, Layout, EmptyState } from '@iba-team/portal-react-components/dist';
import NoLeavesImg from '../../assets/no-leaves.svg';
import { ReactComponent as InfoIcon } from '@iba-team/portal-react-components/dist/svg/outline/information.svg';
import { ReactComponent as PlusIcon } from '@iba-team/portal-react-components/dist/svg/outline/plus.svg';
import { ReactComponent as EditIcon } from '@iba-team/portal-react-components/dist/svg/outline/edit.svg';
import { ReactComponent as CancelIcon } from '@iba-team/portal-react-components/dist/svg/outline/trash.svg';
import { ReactComponent as PlannedIcon } from '../../assets/icons/planned.svg';
import { ReactComponent as CreatePlannedIcon } from '../../assets/icons/create-planned.svg';
import { observer } from 'mobx-react';
import { LeavesPerYearStore } from '../../store/LeavesPerYear';
import LeaveTypes from '../LeaveTypes';
import { format, getYear } from 'date-fns';
import { BREAKPOINT_MD, BREAKPOINT_BIG, BREAKPOINT_MLT_MD } from '../../consts';
import { Button } from '@iba-team/portal-react-components/dist';
import {
  requestModalCall,
  updateModalCall,
  requestDetailsModalCall,
  cancelRequestModalCall, updateForEmployeeModalCall, requestFromPlannedCall
} from '../../containers/MainWrapper/MainWrapper';
import { capitalizeFirstLetter, sendUsageRequest, isFirefox } from '../../utils/functions';
import { LeaveTypesStore } from '../../store/LeaveTypesList';
import { useDeviceSizeThreshold, useDownloadItem } from '../../utils/hooks';
import { LeavesAll, LeavesType } from '../../types';
import { AccessStore } from '../../store/Access';
import { CREATE_LEAVE_REQUEST_ACCESS_CODE } from '../../utils/consts';
import { UserStore } from '../../store/User';
import { useAnalyticsEventTracker } from '../../analytics/analytics';
import { v4 as uuidv4 } from 'uuid';
import { useParams } from 'react-router';
import { createPreciseDate } from '../../utils/formatDateAndTime';
import apiConfig from '../../api/routes';
import { RequestsStore } from '../../store/Requests';

const LM_RELEASE_YEAR = 2022;
export const currentYear = getYear(new Date());

const yearsDiff = currentYear - LM_RELEASE_YEAR;

export const yearsTuple = [currentYear, currentYear+1];

for(let i = 0; i < yearsDiff; i++){
  yearsTuple.push(LM_RELEASE_YEAR + i);
}
yearsTuple.sort((a,b) => b - a);


interface MyLeavesPropsI {
  userId?: string
  columnsProfile?: {title: string, dataIndex: string, key: string, width?: number, className: string}[],
  isEmployee?: boolean,
  isUserDataValid: boolean,
}

const MyLeaves = observer(({columnsProfile, userId, isEmployee, isUserDataValid } : MyLeavesPropsI) => {
  const { t } = useTranslation();
  const selectedYear = currentYear;
  const { leavesAll, getLeavesAll, isFetchedLeavesPerYear, cancelPlannedRequest} = LeavesPerYearStore;
  const isSmallTablet = useDeviceSizeThreshold(BREAKPOINT_MLT_MD);
  const isTablet = useDeviceSizeThreshold(BREAKPOINT_MD);
  const isBigScreen = useDeviceSizeThreshold(BREAKPOINT_BIG);
  const {isAllAccessesFetched, allAccesses} = AccessStore;
  const [isAvailableToCreate, setIsAvailableToCreate] = useState(false);
  const {download} = useDownloadItem();
  const {leaveTypesList, getLeaveTypesList} = LeaveTypesStore;
  const {profile, anotherUser} = UserStore;
  const isProvideId = userId ?? undefined;
  const isReadyToRender = (isFetchedLeavesPerYear && isAllAccessesFetched && !!allAccesses.length) || !isUserDataValid;
  const gaEventTracker = useAnalyticsEventTracker('My_leaves');
  const params = useParams();

  const { getRequests } = RequestsStore;
  useEffect(() => {
    if (userId && profile && userId !== profile.id.toString()) { 
      getRequests(); 
    }
  },[userId, profile?.id]);

  const getAllLeavesWithParams = () => {
    if(!window.location.pathname.includes('leaverequest')){
      getLeavesAll(selectedYear, params.id ? + params.id : undefined);
    }
    else{
      getLeavesAll(selectedYear);
    }
  };


  useEffect(() => {
    getAllLeavesWithParams();
  }, [isProvideId, params.id]);

  useEffect(()=>{
    if (isAllAccessesFetched) {
      const newRequestAcc = !!allAccesses.find(i => (i?.actionCodes.indexOf(CREATE_LEAVE_REQUEST_ACCESS_CODE) !== -1));
      setIsAvailableToCreate(newRequestAcc);
    }
  },[isAllAccessesFetched, allAccesses]);

  useEffect(()=> {
    getLeaveTypesList(isProvideId);
  },[isProvideId]);

  const renderAllAttachmentsCondition = (item: LeavesType) => (item.fileRequisites.length <=2 && item.fileRequisites.length > 0);
  const renderFirstAttachmentCondition = (item: LeavesType) => (item.fileRequisites.length > 2);
  const noAttachmentsCondition = (item: LeavesType) => !item.fileRequisites.length;
  const onAttachmentClick = (e:React.MouseEvent<HTMLSpanElement, MouseEvent>, item: LeavesType) => {
    e.stopPropagation();
    if(renderFirstAttachmentCondition(item)){
      download(item.fileRequisites[0].name, item.fileRequisites[0].ref);
    }
    if(renderAllAttachmentsCondition(item)){
      item.fileRequisites.forEach(i=> download(i.name, i.ref));
    }
  };

  const onCancel = (e: React.MouseEvent<SVGSVGElement, MouseEvent>, item: LeavesType) => {
    e.stopPropagation();
    cancelRequestModalCall.show({
      id: item.id,
      selectedYear: selectedYear,
      isPossibleToCancelForEmployee:item.isPossibleToCancelForEmployee,
      userId: anotherUser.id
    });
  };

  const onCreateFromPlanned = (e: React.MouseEvent<SVGSVGElement, MouseEvent>, item: LeavesType) => {
    e.stopPropagation();
    sendUsageRequest(apiConfig.statisticsUsageService.viewPlannedAbsence);
    requestFromPlannedCall.showImmediately({startDate: item.startDate, endDate: item.endDate, selectedYear: selectedYear});
  };

  const onEdit = (e: React.MouseEvent<SVGSVGElement, MouseEvent>, item: LeavesType ) => {
    e.stopPropagation();
    showRequestModal(item.id);
  };

  const onEditForEmployee = (e:  React.MouseEvent<SVGSVGElement, MouseEvent>, item: LeavesType ) => {
    e.stopPropagation();
    showEditForEmployeeModal(item.id);
  };

  const onInfo = (item: LeavesType) => {
    showDetailsModal({id: item.id});
  };
  const showRequestModal = (id?: number) => {
    id && updateModalCall.show({ currentItemId: id, selectedYear: selectedYear });
  };

  const showEditForEmployeeModal = (id? : number) => {
    id && updateForEmployeeModalCall.show({currentItemId: id, selectedYear: selectedYear});
  };

  const showDetailsModal = (item: {id: number}) => {
    requestDetailsModalCall.show({id: item.id, selectedYear: selectedYear });
  };

  const columns: {title: string, dataIndex: string, key: string, width?: number, className: string}[] = [
    {
      title: `${ t('type') }`,
      dataIndex: 'type',
      key: 'type',
      className: 'my-leaves-table-cell'
    },
    {
      title: `${ t('fromTo') }`,
      dataIndex: 'fromTo',
      key: 'fromTo',
      className: 'my-leaves-table-cell'
    },
    {
      title: `${ t('duration') }`,
      dataIndex: 'duration',
      key: 'duration',
      width: 100,
      className: 'my-leaves-table-cell'
    },
    {
      title: `${ t('status') }`,
      dataIndex: 'status',
      key: 'status',
      width: 86,
      className: 'my-leaves-table-cell'
    },
    ...columnsProfile ?? [],
    {
      title: '',
      dataIndex: 'actions',
      key: 'actions',
      width: 80,
      className: 'my-leaves-table-cell'
    },
  ];

  const renderClassNameByRequestStatus = ((item: LeavesType) => {
    switch (item.status){
    case 'REJECTED':
      return 'red';
    case 'PENDING':
      return 'orange';
    case 'PLANNED':
      return 'grey';
    case 'CANCELLED':
      return 'grey';
    case 'APPROVED':
      return 'green';
    }
  });

  const genBlockLeaves = useMemo(()=>{
    return (el:LeavesAll) => {
      const genId = el.periodDuration + el.id;
      return {
        type: <div className='blockLeaves'><span className='period-type'>{el.periodType}</span>&nbsp;<span className='period-duration'>{el.periodDuration}</span></div>,
        key: genId,
        id: el.periodDuration,
        isActivePeriod: el.isCurrentPeriod,
      };
    };
  },[]);

  const isPlannedLeave = (el: LeavesType) => el.type === 'PLANNED';

  const renderPlanned = useMemo(() => {
    return <div className={'plannedType'}>
      <PlannedIcon/>
      <div>
        Planned absence
      </div>
    </div>;
  },[]);

  const onCancelPlannedRequest = (startDate: string, endDate: string) => {
    return cancelPlannedRequest(startDate, endDate, getAllLeavesWithParams);
  };

  const data = leavesAll.map((el, index) => {
    return [genBlockLeaves(el), !el.leaveRequests.length ?
      {
        type: <div className='blockLeavesNoDate'>{t('noLeaveRequests')}</div>,
        key: index + Math.random(),
        id: index + Math.random(),
        isActivePeriod: undefined,
        disableClick: true,
        isCancelled: false,
        isRejected: false
      }
      :
      el.leaveRequests.map((item,index)=> {
        return ({
          key: uuidv4(),
          id: item.id,
          disableClick: columnsProfile && isEmployee,
          type:
            <div className="leave-type">
              {isPlannedLeave(item) ? renderPlanned : <LeaveTypes leaveTypes={leaveTypesList} type={item.requestName} infoMessageVisible={true}/>}
            </div>,
          fromTo:
            <>
              {format(createPreciseDate(item.startDate), 'dd.MM.yyyy')}-{format(createPreciseDate(item.endDate), 'dd.MM.yyyy')}
            </>,
          duration:
            <div className="leave-duration">
              <Tooltip title={t('workingDays')}>
                {item.duration}
              </Tooltip>
            </div>,
          status:
            <Tag borderType={isPlannedLeave(item) ? 'dashed' : 'default'}
              text={capitalizeFirstLetter(item.status.toLowerCase())}
              variant={'minimal'}
              color={renderClassNameByRequestStatus(item)}
              size={'small'} key={item.status} />,
          isCancelled: item.status && item.status === 'CANCELLED' ? true : false,
          isRejected: item.status && item.status === 'REJECTED'? true : false,
          attachment: <>
            {renderAllAttachmentsCondition(item) &&
              <div className="leave-attachment">
                {item.fileRequisites.map(i => <span className="leave-attachment__item" onClick={(e) => onAttachmentClick(e, item)}>
                  {i.name}</span>)
                }
              </div>
            }
            {renderFirstAttachmentCondition(item) &&
              <div className="leave-attachment multiple">
                <span className="leave-attachment__item" onClick={(e) => onAttachmentClick(e, item)}>
                  {item.fileRequisites[0].name}
                </span>
                <span style={{marginLeft: '3px'}}> + {item.fileRequisites.length - 1 } files</span>
              </div>
            }
            {noAttachmentsCondition(item) &&  <div className="leave-attachment">-</div> }
          </>,
          comment: <div className={isBigScreen ? 'leave-comment leave-comment_small' : 'leave-comment'}>{item.comment ? item.comment : '-'}</div>,
          actions: <div className={'leave-actions'}>
            {item.type === 'PLANNED' && (!userId || userId === profile.id.toString()) ?
              <>
                <Tooltip title={ t('cancel') }>
                  <CancelIcon className={'cancelIcon'} onClick={(e) => onCancelPlannedRequest(item.startDate, item.endDate)}/>
                </Tooltip>
                <Tooltip title={ t('createRequest') }>
                  <CreatePlannedIcon className={'createIcon'} onClick={(e) => onCreateFromPlanned(e, item)}/>
                </Tooltip>
              </>
              :
              <>
                {
                  item.isPossibleToCancel && <Tooltip title={ t('cancel') }>
                    <CancelIcon className={'cancelIcon'} onClick={(e) => onCancel(e, item)}/>
                  </Tooltip>
                }
                {
                  !item.isPossibleToCancel && item.isPossibleToCancelForEmployee && (Number(userId) !== profile.id) && <Tooltip title={ t('Cancel for Employee') }>
                    <CancelIcon className={'cancelIcon'} onClick={(e) => onCancel(e, item)}/>
                  </Tooltip>
                }
                {
                  item.isPossibleToEdit && <Tooltip title={ t('edit') }>
                    <EditIcon onClick={(e) => onEdit(e, item)} />
                  </Tooltip>
                }
                {
                  !item.isPossibleToEdit && item.isPossibleToEditForEmployee && (Number(userId) !== profile.id) && <Tooltip title={ 'Edit for employee' }>
                    <EditIcon  onClick={(e) => onEditForEmployee(e, item)} />
                  </Tooltip>
                }
                {item.type !== 'PLANNED' && !isEmployee && columnsProfile && <Tooltip title={ t('details') }>
                  <InfoIcon onClick={()=> onInfo(item)} className={item.isPostfactum && item.status === 'PENDING' ? 'info_red' : '' }/>
                </Tooltip> }
                {item.type !== 'PLANNED' &&  !columnsProfile && <Tooltip title={ t('details') }>
                  <InfoIcon onClick={()=> onInfo(item)} className={item.isPostfactum && item.status === 'PENDING' ? 'info_red' : '' }/>
                </Tooltip>}
              </>
            }
          </div>,
        });
      })].flat();
  }).flat();

  return (
    <Layout isLoading={!isReadyToRender} allowBaseMargins header={{layoutName: <h3>
      {columnsProfile ? t('requestHistory') : t('myLeaves') }
    </h3>, right: ( (userId && Number(userId) === profile.id) || !userId ) &&
        <Tooltip title={!isAvailableToCreate && t('mdaCreationTooltip')}>
          <Button icon={<PlusIcon />} disabled={!isAvailableToCreate} className={'newRequest_btn'} theme={{ variant: 'primary', color: 'theme' }} onClick={() => {
            requestModalCall.show({
              selectedYear: selectedYear
            });
            gaEventTracker('request_modal_call');
          }}>
            <div>{t('newRequest')}</div>
          </Button>
        </Tooltip>
    }} className={`my-leaves ${!isReadyToRender && 'my-leaves-wrapper'}`} >
      { isUserDataValid ?
        <>
          { data.length ?
            <Table
              rowClassName={(record:any, index) => {
                const { isActivePeriod, isCancelled, isRejected, leaveRequests } = record;
                const baseClass = 'my-leaves-table-row-class';
                const hasLeaveRequests = leaveRequests?.length > 0;
                if (isActivePeriod !== undefined && !hasLeaveRequests) return isActivePeriod ? 'active-period' : 'default-period';
                if (isCancelled) return `${baseClass} cancelled-status-table-row`;
                if (isRejected) return `${baseClass} rejected-status-table-row`;
                return baseClass;
              }}
              columns={!isSmallTablet ? columns: columns.filter(i => i.key === 'type' || i.key === 'fromTo' || i.key === 'status')}
              dataSource={data}
              scroll={isTablet ? { y: 200 } : { y: 460 }}
              pagination={false}
              onRow={(record,ind ) => {
                if(!isFirefox()){
                  const height = document.querySelector('.active-period')?.scrollHeight;
                  const styles: CSSProperties = {};
                  const index = leavesAll.findIndex(i => i.periodDuration === record.id) ?? 0;
                  if(record.isActivePeriod !== undefined){
                    styles['position'] = 'sticky';
                    styles['top'] = `${height ? index * height : index * 47}px`;
                  }
                  return {
                    onClick: () => {
                      !record?.disableClick && showDetailsModal(record as LeavesType);
                    },
                    style: styles
                  };
                }
                return {
                  onClick: () => {
                    !record?.disableClick && showDetailsModal(record as LeavesType);
                  }
                };
              }}
            /> :
            <EmptyState title={t('noLeaves')} description={!userId ? t('noLeavesDescription') : t('noLeavesMsg')} icon={<img src={ NoLeavesImg } alt="noLeaves" className="no-leaves__img"/>}/>
          }
        </>
        :
        <EmptyState
          className={'empty'}
          title={t('messagesForBanners.noData.title')}
          description={t('messagesForBanners.noData.body')}
        />
      }
      
      
    </Layout>
  );
});

export default MyLeaves;
